"use client";

import React, { ReactElement, useEffect, useState, useTransition } from "react";
import Link from "next/link";
import { useParams, useRouter } from "next/navigation";
import { LoadingSpinner } from "@/utils/components/LoadingSpinner";
import { NoDataDisplay } from "@/components/NoDataDisplay";
import {
  Alert,
  CompetitionCard,
  Dropdown,
} from "@nrldigitaltechnology/ui-community";
import { ageLevels } from "@/utils/constants";
import { getS3URL } from "@/utils/utilities";
import { useStateRegionStore } from "@/store/store";
import ImageWithFallback from "@/layouts/ImageFallBack";

export function ClubClient(clubProps: any) {
  const params = useParams();
  const router = useRouter();
  const [isPending, startTransition] = useTransition();
  const{club:clubState , setTeam , setCompetition} = useStateRegionStore();

  const [isLoading, setLoading] = useState<boolean>(false);
  const [teams, setTeams] = useState([]);
  const [club, setClub] = useState<any>(null);
  const [ageLevelFilter, setAgeLevelOptions] = useState<any[]>([]);
  const [seasonFilter, setSeasonOptions] = useState<any[]>([]);
  const [selectedAgeLevel, setAgeLevel] = useState<any>(null);
  const [selectedSeason, setSeason] = useState<any>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [alertTitle, setAlertTitle] = useState("");
  const [alertMessage, setAlertMessage] = useState("");
  const [competitionLinks, setCompetitionLinks] = useState<ReactElement<
    any,
    any
  > | null>(null);

  useEffect(() => {
    const updateLoading = clubProps?.data?.isLoading ? true : false;
    setLoading(updateLoading);
  }, [clubProps?.data?.isLoading]);

  useEffect(() => {
    if (clubProps?.data?.teams) {
      const uniqueSeasons = new Set();
      const uniqueAgeLvl = new Set();
      const teamCardData = clubProps.data.teams
        .sort((a: any, b: any) => {
          if (a.isActive && !b.isActive) return -1;
          else if (!a.isActive && b.isActive) return 1;
          else return a?.name.localeCompare(b?.name);
        })
        .map((t: any) => {
          if (t.season) uniqueSeasons.add(t.season);
          if (t.ageLvl) uniqueAgeLvl.add(t.ageLvl);
          if (
            !club &&
            t?.orgtree?.club &&
            t.orgtree.club._id === Number(params.id)
          )
            setClub(t.orgtree.club);

          return {
            _id: t._id,
            name: t.name,
            logo:
              t.teamLogo ||
              (t?.orgtree?.club?._id && getS3URL(t.orgtree.club._id)) ||
              getS3URL(t?.orgtree?.association?._id),
            entityLvl1: t.orgtree.club.name,
            entityLvl2: t.orgtree.association.name,
            competition:
              t?.competitions &&
                Array.isArray(t.competitions) &&
                t.competitions.length
                ? t.competitions
                : [],
          };
        });
      setTeams(teamCardData);

      const seasonOptions = [...uniqueSeasons]
        .sort((a: any, b: any) => b - a)
        .map((s) => ({ label: s, value: s }));
      setSeasonOptions(seasonOptions);
      if (seasonOptions.length) setSeason(seasonOptions[0]);

      const ageLvlOptions = ageLevels.filter((a) =>
        [...uniqueAgeLvl].includes(a.label),
      );
      setAgeLevelOptions(ageLvlOptions);
    }
  }, [clubProps]);

  useEffect(() => {
    const filteredTeams = clubProps?.data?.teams.filter((c: any) => {
      const ageLvlCheck = selectedAgeLevel?.label
        ? selectedAgeLevel.label === c.ageLvl
        : true;
      const seasonCheck = selectedSeason?.label
        ? selectedSeason.label === c.season
        : true;
      return seasonCheck && ageLvlCheck;
    });
    const teamCardData = filteredTeams
      .sort((a: any, b: any) => {
        if (a.isActive && !b.isActive) return -1;
        else if (!a.isActive && b.isActive) return 1;
        else return a?.name.localeCompare(b?.name);
      })
      .map((t: any) => ({
        _id: t._id,
        name: t.name,
        logo:
        t.teamLogo ||
          (t?.orgtree?.club?._id && getS3URL(t.orgtree.club._id)) ||
          "/assets/logo/noimage.png",
        entityLvl1: t?.orgtree?.club?.name,
        entityLvl2: t?.orgtree?.association?.name,
        competition:
          t?.competitions &&
            Array.isArray(t.competitions) &&
            t.competitions.length
            ? t.competitions
            : [],
      }));
    setTeams(teamCardData);
  }, [selectedAgeLevel, selectedSeason]);

  const handleTeamClick = (cardData: any, competitionId?: number) => {
    // Set team in store
    setCompetition({
      value: cardData._id.toString(),
      label: cardData.name,
      logo: cardData.logo || getS3URL(parseInt(cardData._id)),
    });

    // Navigate to competition page
    const path = competitionId
      ? `/competitions/${competitionId}?filter=teams&team=${cardData._id}`
      : `/competitions/${cardData.competition[0]._id}?filter=teams&team=${cardData._id}`;
    
    startTransition(() => {
      router.push(path);
    });
  };

  const openDialogAlert = (data: any) => {
    setOpenDialog(true);
    if (data?.competition?.length) {
      setAlertTitle("Select a Competition");
      setAlertMessage(
        `According to our records the team, ${data.name}, participates in more than one competition. Please select a competition to view:`,
      );

      setClub({
        value: data._id,
        label: data.name,
        logo: getS3URL(parseInt(data?.id)),
      });
      const linkList = data.competition.map(
        (comp: { _id: number; name: string }) => {
          return (
            <button
              key={`comp-link-${comp._id}`}
              onClick={() => {
                setOpenDialog(false);
                handleTeamClick(data, comp._id);
              }}
              className="text-left"
            >
              <span className="text-sm uppercase text-[#007700] hover:underline">
                {comp.name}
              </span>
            </button>
          );
        },
      );
      if (linkList)
        setCompetitionLinks(
          <div className="flex flex-col gap-2 pb-4">{linkList}</div>,
        );
    } else {
      setAlertTitle("No Competitions Found");
      setAlertMessage(
        `According to our records the team, ${data.name}, currently does not participate in any active competitions.`,
      );
    }
  };

  return (
    <div className="flex w-full flex-col items-center bg-[#F8F8F8]">
      <div className="w-[90%] max-w-[82.5rem] flex flex-col my-10">
        {clubState?.logo && (
          <div className="flex items-center gap-4">
            <div className="w-[60px] h-[60px] rounded-md bg-white p-[8px]">
            <ImageWithFallback
                src={clubState?.logo}
                alt="Association Logo"
                style="object-contain w-full h-full" fallbackSrc={"/assets/logo/noimage.png"} />
              {/* <img
                src={clubState?.logo || "/assets/logo/noimage.png"}
                alt="Club Logo"
                className="w-full h-full object-contain"
              /> */}
            </div>
            <span className="font-base/[30px] font-medium uppercase tracking-[1.2px]">
              {clubState?.label}
            </span>
          </div>
        )}
        <div className="flex lg:flex-row flex-col gap-2 w-full bg-white rounded-b-md px-[24px] py-[16px] mt-6">
          {seasonFilter.length ? (
            <Dropdown
              placeHolder="Season"
              options={seasonFilter}
              onSelect={(v) => {
                setSeason(v);
              }}
              selectedOption={selectedSeason}
              customStyles={{
                containerStyle: "lg:w-[200px]",
              }}
            />
          ) : null}
          {ageLevelFilter.length ? (
            <Dropdown
              placeHolder="Age"
              options={ageLevelFilter}
              onSelect={(v) => setAgeLevel(v)}
              selectedOption={selectedAgeLevel}
              customStyles={{
                containerStyle: "lg:w-[200px]",
              }}
            />
          ) : null}
        </div>
        {!isLoading ? (
          Array.isArray(teams) && teams.length ? (
            <React.Fragment>
              <div className="grid grid-cols-1 md:grid-cols-2  lg:grid-cols-3 gap-4 mt-6">
                {teams.map((cardData: any, index: number) => (
                  <React.Fragment key={`${cardData._id}_${index}`}>
                    {cardData?.competition?.length === 1 ? (
                      <button
                        onClick={() => handleTeamClick(cardData)}
                        className="block w-full text-left"
                      >
                        <CompetitionCard
                          variant="team"
                          cardData={cardData}
                          customStyles={{
                            containerStyle: "h-full justify-between",
                            imgStyle: "sm:max-w-[88px] max-w-[68px]",
                          }}
                        />
                      </button>
                    ) : (
                      <div
                        onClick={() => {
                          openDialogAlert(cardData);
                        }}
                        className="cursor-pointer"
                      >
                        <CompetitionCard
                          variant="team"
                          cardData={cardData}
                          customStyles={{
                            containerStyle: "h-full justify-between",
                            imgStyle: "sm:max-w-[88px] max-w-[68px]",
                          }}
                        />
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
              <Alert
                open={openDialog}
                setOpen={setOpenDialog}
                title={alertTitle}
                message={alertMessage}
                additionalUiContents={competitionLinks}
                buttonContents={[
                  {
                    id: "alert-back-button",
                    variant: "borderOnly",
                    size: "sm",
                    label: "Back",
                    customStyles: { containerStyle: "w-full" },
                  },
                ]}
              />
            </React.Fragment>
          ) : (
            <NoDataDisplay dataSearch="teams" customStyle="mt-6" />
          )
        ) : (
          <LoadingSpinner />
        )}
      </div>
    </div>
  );
}
